import React, { useState, useEffect, useRef } from 'react';
import { Button, Hidden, IconButton, MenuItem, Typography } from '@mui/material';
import { AddBox } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import MaterialTable from 'material-table';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { isAdmin, TableCount, TablePage } from "../../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../../config/Translate";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../../config/design/styles";
import { tableIcons } from "../../../../../config/design/TableIcons";
import { defaultVariables } from "../../../../../config";
import { useIsSmall } from "../../../../../config/hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import AddStockButton from "./AddStockButton";
import { calcStockPrice, currencySymbol, price, scrollTop, showTableDate } from "../../../../../config/values";
import TransferStockButton from "./TransferStockButton";
import StockHistoryButton from "./StockHistoryButton";
import RayaField from "../../../../components/elements/fields/RayaField";
import Grid from "@mui/material/Grid2";

const Stocks = ({ classes }) => {
    const isSmall = useIsSmall();
    const { t } = useTranslation();

    const [columns] = useState([
        {
            title: t("stockNumber"),
            field: "number",
        },
        {
            title: t("stockType"),
            field: "is_complete",
            render: rowData => (
                <Typography>
                    {t(rowData.type)} / {!!rowData.is_complete ? t('completeStockShort') : t('incompleteStockShort')}
                </Typography>
            )
        },
        {
            title: t('user'),
            field: 'user',
            filterPlaceholder: t('search'),
            sorting: false,
            hidden: !isAdmin(),
            render: rowData => (
                <Typography>
                    {rowData.user.first_name} {rowData.user.last_name}
                </Typography>
            )
        },
        {
            title: t('current_price'),
            editable: 'never',
            field: 'current_price',
            render: rowData => (
                <Typography>
                    {price(rowData.current_price)} {currencySymbol}
                </Typography>
            )
        },
        {
            title: t('paid_time_price'),
            editable: 'never',
            field: 'paid_price',
            render: rowData => (
                <Typography>
                    {price(rowData.ownership.price)} {currencySymbol} {!rowData.ownership.is_cleared && `(${t('hasInstallment')})`}
                </Typography>
            )
        },
        {
            title: t('created_at'),
            editable: 'never',
            field: 'created_at',
            render: rowData => (
                <Typography className={classes.dateTable}>
                    {showTableDate(rowData.created_at)}
                </Typography>
            )
        },
        {
            title: t('stockHistory'),
            editable: 'never',
            hidden: !isAdmin(),
            field: 'activities',
            render: rowData => (
                <StockHistoryButton ownerships={rowData.ownerships} />
            )
        },
    ]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const tableRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();

    // ✅ NEW: Filters
    const [isCompleteFilter, setIsCompleteFilter] = useState('');
    const [stockTypeFilter, setStockTypeFilter] = useState('');

    const loadData = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    useEffect(() => {
        loadData();
        scrollTop();
    }, []);

    return (
        <>
            {loading ? "Loading" :
                <div className="main-table">
                    {/* ✅ New filtering fields */}
                    <div
                        style={{
                            padding: 10,
                            backgroundColor: "#f9f9f9",
                            borderRadius: 10,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item size={{xs:6,md:4,lg:2}}>
                                <RayaField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label={t('is_complete')}
                                    name="is_complete"
                                    value={isCompleteFilter}
                                    onChange={(e) => {
                                        setIsCompleteFilter(e.target.value);
                                        setTimeout(() => loadData(), 0);
                                    }}
                                    select
                                >
                                    <MenuItem value="">{t('all')}</MenuItem>
                                    <MenuItem value={1}>{t('yesComplete')}</MenuItem>
                                    <MenuItem value={0}>{t('noComplete')}</MenuItem>
                                </RayaField>
                            </Grid>
                            <Grid item size={{xs:6,md:4,lg:2}}>
                                <RayaField
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    label={t('stock_type')}
                                    name="stock_type"
                                    value={stockTypeFilter}
                                    onChange={(e) => {
                                        setStockTypeFilter(e.target.value);
                                        setTimeout(() => loadData(), 0);
                                    }}
                                    select
                                >
                                    <MenuItem value="">{t('all')}</MenuItem>
                                    <MenuItem value="normal">{t('normalStock')}</MenuItem>
                                    <MenuItem value="special">{t('specialStock')}</MenuItem>
                                </RayaField>
                            </Grid>
                        </Grid>
                    </div>

                    {/* Buttons for mobile */}
                    {!!isSmall &&
                        <div style={{ display: "flex", gap: 10 }}>
                            <AddStockButton reload={() => loadData()} />
                            <TransferStockButton reload={() => loadData()} />
                        </div>
                    }

                    <MaterialTable
                        tableRef={tableRef}
                        icons={tableIcons}
                        title={
                            (!!isAdmin() && !isSmall) ?
                                <div style={{ display: "flex", gap: 10 }}>
                                    <AddStockButton reload={() => loadData()} />
                                    <TransferStockButton reload={() => loadData()} />
                                </div>
                                :
                                " "
                        }
                        localization={getDataTableLocalization()}
                        columns={columns}
                        data={query =>
                            new Promise((resolve, reject) => {
                                axios.get(`${defaultVariables.url}/stocks/quickView`, {
                                    headers: {
                                        Accept: 'application/json',
                                        Authorization: `Bearer ${localStorage.getItem('ACT')}`
                                    },
                                    params: {
                                        page: query.page + 1,
                                        per_page: query.pageSize,
                                        search: query.search,
                                        is_complete: isCompleteFilter,
                                        stock_type: stockTypeFilter,
                                    }
                                }).then(response => {
                                    const rows = response.data.data.map(row => ({
                                        id: row.id,
                                        user: row.user,
                                        number: row.number,
                                        type: row.type,
                                        is_complete: row.is_complete,
                                        created_at: row.created_at,
                                        current_price: calcStockPrice(row.type, 1, row.is_complete),
                                        ownership: row.ownership,
                                        ownerships: row.ownerships,
                                    }));
                                    resolve({
                                        data: rows,
                                        page: response.data.current_page - 1,
                                        totalCount: response.data.total
                                    });
                                }).catch(error => {
                                    console.log(error.response);
                                    reject();
                                });
                            })
                        }
                        options={{
                            pageSize: TablePage,
                            pageSizeOptions: TableCount,
                            defaultExpanded: false,
                            draggable: false,
                        }}
                    />
                </div>
            }
        </>
    );
};

export default withStyles(Styles)(Stocks);
